import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { fetchUserAccount, fetchUserCards } from "./../slices/user";
import { canopy } from "./../theme";

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{children}</Typography>
);

const brandColor = (brand) => {
  const b = (brand || "").toUpperCase();
  if (b.includes("VISA")) return canopy.greenDark;
  if (b.includes("MASTER")) return canopy.inkSoft;
  if (b.includes("AMEX") || b.includes("AMERICAN")) return canopy.warn;
  return canopy.faint;
};

const brandLabel = (brand) => {
  const b = (brand || "").toUpperCase();
  if (b.includes("MASTER")) return "MC";
  if (b.includes("AMERICAN")) return "AMEX";
  if (b.includes("DISCOVER")) return "DISC";
  return b.slice(0, 4) || "CARD";
};

function ContactForm() {
  const { user } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phoneNumber: user.phoneNumber || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form Submitted", formData);
    // Add further logic here (e.g., API call)
  };

  return (
    <Stack spacing={1.25}>
      <SectionTitle>Contact information</SectionTitle>
      <Card sx={{ p: 2.5 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id="firstName"
                name="firstName"
                label="First name"
                fullWidth
                variant="outlined"
                value={formData.firstName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id="lastName"
                name="lastName"
                label="Last name"
                fullWidth
                variant="outlined"
                value={formData.lastName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                id="email"
                name="email"
                label="Email"
                fullWidth
                variant="outlined"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="phoneNumber"
                name="phoneNumber"
                label="Phone number"
                fullWidth
                variant="outlined"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained">
                Save changes
              </Button>
            </Grid>
          </Grid>
        </form>
      </Card>
    </Stack>
  );
}

function LinkedAccount() {
  const { linkedAccount } = useSelector((state) => state.user);
  return (
    <Stack spacing={1.25}>
      <SectionTitle>Your property</SectionTitle>
      <Card sx={{ p: "18px 20px", display: "flex", alignItems: "center", gap: 1.75 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "14px",
            bgcolor: canopy.soft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: canopy.green,
          }}
        >
          <HomeOutlinedIcon />
        </Box>
        <Typography sx={{ fontWeight: 700 }}>
          {linkedAccount
            ? `${linkedAccount.houseNumber} ${linkedAccount.propertyAddress}`
            : "No linked account"}
        </Typography>
      </Card>
    </Stack>
  );
}

function SavedCards() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { savedCards } = useSelector((state) => state.user);
  const squareCustomerId = user?.squareCustomerId;

  useEffect(() => {
    if (squareCustomerId) {
      dispatch(fetchUserCards(squareCustomerId));
    }
  }, [dispatch, squareCustomerId]);

  return (
    <Stack spacing={1.25}>
      <SectionTitle>Saved cards</SectionTitle>
      {savedCards && savedCards.length > 0 ? (
        savedCards.map((card) => (
          <Card
            key={card.id}
            sx={{ p: "18px 20px", display: "flex", alignItems: "center", gap: 1.75 }}
          >
            <Box
              sx={{
                width: 48,
                height: 34,
                borderRadius: "8px",
                bgcolor: brandColor(card.cardBrand),
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.06em",
                flexShrink: 0,
              }}
            >
              {brandLabel(card.cardBrand)}
            </Box>
            <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                {card.cardBrand} ending in {card.last4}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                Expires {card.expMonth}/{card.expYear}
              </Typography>
            </Stack>
            {!card.enabled && (
              <Chip
                label="DISABLED"
                size="small"
                sx={{ bgcolor: canopy.dangerSoft, color: canopy.danger, fontSize: 11 }}
              />
            )}
          </Card>
        ))
      ) : (
        <Card sx={{ p: 2.5 }}>
          <Typography sx={{ color: "text.secondary", fontWeight: 600 }}>
            No saved cards yet — cards are saved when you pay dues or set up autopay.
          </Typography>
        </Card>
      )}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, px: 0.75 }}>
        <LockOutlinedIcon sx={{ fontSize: 18, color: canopy.faint, mt: "1px" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
          Cards are stored securely with Square. Shelby Estates never sees your full card
          number.
        </Typography>
      </Box>
    </Stack>
  );
}

function Subscriptions() {
  const { subscriptions } = useSelector((state) => state.user);

  const formatDate = React.useCallback(
    (date) => {
      const dateParts = date.split("-");

      // Create the date object manually (Year, Month - 1, Day)
      const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

      // Formatting the date to "Nov 1, 2024"
      const formattedDate = dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      return formattedDate;
    },
    [subscriptions],
  );

  const active = subscriptions?.find((s) => s.status === "ACTIVE");
  const pending = subscriptions?.find((s) => s.status === "PENDING");
  const current = active || pending;

  return (
    <Stack spacing={1.25}>
      <SectionTitle>Autopay</SectionTitle>
      <Card sx={{ p: 2.75, bgcolor: canopy.soft, border: "none", boxShadow: "none" }}>
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "14px",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: canopy.green,
              flexShrink: 0,
            }}
          >
            <AutorenewIcon />
          </Box>
          <Stack spacing={0.25} sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 17 }}>
              {current
                ? current.canceledDate
                  ? "Autopay is ending"
                  : active
                    ? "Autopay is on"
                    : "Autopay is pending"
                : "Autopay is off"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: canopy.inkSoft }}>
              {current
                ? `$15.00 on the 1st of every month${
                    current.startDate ? ` · started ${formatDate(current.startDate)}` : ""
                  }${
                    current.canceledDate
                      ? ` · ends ${formatDate(current.canceledDate)}`
                      : ""
                  }`
                : "Turn it on from your account page to pay $15.00 automatically."}
            </Typography>
          </Stack>
          {current && (
            <Chip
              label={current.canceledDate ? "ENDING" : active ? "ACTIVE" : "PENDING"}
              size="small"
              sx={{
                bgcolor: "background.paper",
                color: current.canceledDate
                  ? canopy.warn
                  : active
                    ? canopy.greenDark
                    : canopy.warn,
                fontSize: 11,
              }}
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  useEffect(() => {
    dispatch(fetchUserAccount(user?.uid));
  }, []);
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 10 }}>
      <Stack
        spacing={3}
        sx={{
          px: 3,
          pt: 3,
          maxWidth: (t) => t.breakpoints.values.sm,
          mx: "auto",
        }}
      >
        <Typography variant="h5">Autopay &amp; cards</Typography>
        <Subscriptions />
        <SavedCards />
        <LinkedAccount />
        <ContactForm />
      </Stack>
    </Box>
  );
};

export default ProfilePage;
